/**
 * GanttTimeline — Gantt professionale con:
 *  - colonna sinistra sticky (nome lavorazione + azioni inline)
 *  - viste multiple (giornaliera/settimanale/mensile/trimestrale/annuale)
 *  - drag & drop orizzontale della barra (riprogramma spostando)
 *  - **NEW v1.9.12**: resize handles per allargare/restringere la barra (data_inizio / data_fine)
 *  - **NEW v1.9.12**: trascinamento verticale del grip per riordinare le lavorazioni
 *  - **NEW v1.9.12**: dipendenze visuali (frecce SVG) + modalità "collega"
 *
 * Props:
 *   items          – array di attività (già sortate per `ordine` dal backend)
 *   viewMode       – "giornaliera" | "settimanale" | "mensile" | "trimestrale" | "annuale"
 *   center         – Date (default: today)
 *   onShiftDates   – ({attivita_id, data_inizio, data_fine, shift_days}) => Promise|void  (drag barra intera)
 *   onResizeDates  – ({attivita_id, data_inizio?, data_fine?}) => Promise|void            (resize bordo)
 *   onReorder      – (items:[{attivita_id, ordine}]) => Promise|void                       (drop verticale)
 *   onLinkDeps     – ({attivita_id, dipendenze:string[]}) => Promise|void                  (aggiorna deps)
 *   onRowClick     – (attivita) => void
 *   onEditRow      – (attivita) => void
 *   onDeleteRow    – (attivita) => void
 *   readOnly       – bool
 */
import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  DndContext,
  useDraggable,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Pencil, Trash2, GripVertical, Link2, Link2Off } from "lucide-react";
import {
  computeScale,
  activityBox,
  pxToDays,
  snapDaysToView,
  shiftDate,
  parseISO,
  isoDate,
  addDays,
} from "@/lib/ganttScale";

const STATO_LABELS = {
  programmata: "Programmata",
  in_corso: "In corso",
  eseguita: "Eseguita",
  consegnato: "Consegnato",
  attesa_materiale: "Attesa materiale",
  sospesa: "Sospesa",
  in_ritardo: "In ritardo",
  da_iniziare: "Programmata",
  completata: "Eseguita",
};

const NUM_COL_PX = 48;
const WORK_COL_PX = 310;
const RESP_COL_PX = 190;
const NAME_COL_PX = NUM_COL_PX + WORK_COL_PX + RESP_COL_PX;
const TIMELINE_MIN_PX = 900;
const ROW_H = 28;

/* ============================================================
 * DraggableBar — trascina l'intera barra (riprogramma spostando)
 * + resize handles su bordo sinistro e destro
 * ============================================================ */
function DraggableBar({ activity, box, viewMode, onClick, readOnly, onResizeStart }) {
  const colore = activity.colore_stato || "#94a3b8";
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `move-${activity.attivita_id}`,
    disabled: readOnly,
    data: { type: "move", attivita_id: activity.attivita_id },
  });
  const dx = transform ? transform.x : 0;

  return (
    <div
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (isDragging) return;
        e.stopPropagation();
        onClick?.(activity);
      }}
      className={`absolute top-1.5 h-4 rounded-sm shadow-sm border border-black/10 group ${isDragging ? "opacity-70 ring-2 ring-blue-400" : "hover:brightness-110"}`}
      style={{
        left: `${box.offsetPct}%`,
        width: `${box.widthPct}%`,
        background: colore,
        transform: `translateX(${dx}px)`,
        transition: isDragging ? "none" : "transform 120ms ease",
      }}
      title={`${activity.nome} · ${activity.data_inizio} → ${activity.data_fine}`}
      data-testid={`gantt-bar-${activity.attivita_id}`}
    >
      {/* Handle sinistro (resize inizio) */}
      {!readOnly && (
        <div
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onResizeStart?.(e, activity, "left"); }}
          className="absolute left-0 top-0 w-1.5 h-full cursor-ew-resize bg-black/20 opacity-0 group-hover:opacity-100 hover:bg-white/40 rounded-l-sm z-10"
          title="Trascina per cambiare la data di inizio"
          data-testid={`gantt-resize-left-${activity.attivita_id}`}
        />
      )}
      {/* Area centrale (drag orizzontale intera barra) */}
      <div
        {...(readOnly ? {} : { ...listeners, ...attributes })}
        className={`absolute inset-x-1.5 inset-y-0 cursor-${readOnly ? "pointer" : "grab"}`}
      />
      {/* Handle destro (resize fine) */}
      {!readOnly && (
        <div
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onResizeStart?.(e, activity, "right"); }}
          className="absolute right-0 top-0 w-1.5 h-full cursor-ew-resize bg-black/20 opacity-0 group-hover:opacity-100 hover:bg-white/40 rounded-r-sm z-10"
          title="Trascina per cambiare la data di fine"
          data-testid={`gantt-resize-right-${activity.attivita_id}`}
        />
      )}
      {/* Progress fill */}
      <div
        className="h-full bg-black/25 rounded-sm pointer-events-none"
        style={{ width: `${Math.max(0, Math.min(100, activity.percentuale_avanzamento || 0))}%` }}
      />
      <div className="absolute inset-0 flex items-center px-1 text-[10px] font-semibold text-white truncate select-none pointer-events-none">
        {activity.nome}
      </div>
    </div>
  );
}

/* ============================================================
 * GanttTimeline
 * ============================================================ */
export default function GanttTimeline({
  items = [],
  viewMode = "mensile",
  center,
  onShiftDates,
  onResizeDates,
  onReorder,
  onLinkDeps,
  onRowClick,
  onEditRow,
  onDeleteRow,
  readOnly = false,
}) {
  const scale = useMemo(() => computeScale(viewMode, center || new Date()), [viewMode, center]);
  const timelineRef = useRef(null);
  const [pendingDrag, setPendingDrag] = useState(null);
  const [linkingFrom, setLinkingFrom] = useState(null);
  const [rowDrag, setRowDrag] = useState({ srcId: null, overId: null });
  const [resizePreview, setResizePreview] = useState(null); // {id, di, df}

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  /* -------- Drag orizzontale barra intera -------- */
  const handleDragEnd = async (evt) => {
    const containerW = timelineRef.current?.clientWidth || 1;
    const rawDays = pxToDays(evt.delta?.x || 0, containerW, scale.totalDays);
    const shift = snapDaysToView(rawDays, viewMode);
    setPendingDrag(null);
    if (!shift || readOnly) return;
    const attId = String(evt.active.id).replace(/^move-/, "");
    const att = items.find((a) => a.attivita_id === attId);
    if (!att) return;
    const nuovoInizio = shiftDate(att.data_inizio, shift);
    const nuovaFine = shiftDate(att.data_fine, shift);
    if (nuovoInizio === att.data_inizio && nuovaFine === att.data_fine) return;
    await onShiftDates?.({ attivita_id: att.attivita_id, data_inizio: nuovoInizio, data_fine: nuovaFine, shift_days: shift });
  };

  /* -------- Resize handles (mouse move nativo) -------- */
  const handleResizeStart = (e, activity, side) => {
    if (readOnly || !onResizeDates) return;
    const startX = e.clientX;
    const containerW = timelineRef.current?.clientWidth || 1;
    const initDi = activity.data_inizio;
    const initDf = activity.data_fine;
    const pxPerDay = containerW / scale.totalDays;

    const move = (ev) => {
      const dx = ev.clientX - startX;
      const rawDays = Math.round(dx / Math.max(pxPerDay, 0.1));
      if (side === "left") {
        const newDi = shiftDate(initDi, rawDays);
        if (parseISO(newDi) && parseISO(initDf) && parseISO(newDi) <= parseISO(initDf)) {
          setResizePreview({ id: activity.attivita_id, di: newDi, df: initDf });
        }
      } else {
        const newDf = shiftDate(initDf, rawDays);
        if (parseISO(newDf) && parseISO(initDi) && parseISO(newDf) >= parseISO(initDi)) {
          setResizePreview({ id: activity.attivita_id, di: initDi, df: newDf });
        }
      }
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      setResizePreview((cur) => {
        if (!cur || cur.id !== activity.attivita_id) return null;
        // Commit se cambiato davvero
        if (side === "left" && cur.di !== initDi) {
          onResizeDates({ attivita_id: activity.attivita_id, data_inizio: cur.di, data_fine: cur.df });
        } else if (side === "right" && cur.df !== initDf) {
          onResizeDates({ attivita_id: activity.attivita_id, data_inizio: cur.di, data_fine: cur.df });
        }
        return null;
      });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  /* -------- Riordino verticale (HTML5 native drag) -------- */
  const handleRowDragStart = (id) => (e) => {
    if (readOnly || !onReorder) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
    setRowDrag({ srcId: id, overId: null });
  };
  const handleRowDragOver = (id) => (e) => {
    if (readOnly || !onReorder) return;
    e.preventDefault();
    if (rowDrag.overId !== id) setRowDrag((s) => ({ ...s, overId: id }));
  };
  const handleRowDrop = (targetId) => async (e) => {
    e.preventDefault();
    const srcId = e.dataTransfer.getData("text/plain") || rowDrag.srcId;
    setRowDrag({ srcId: null, overId: null });
    if (!srcId || srcId === targetId || !onReorder) return;
    const ids = items.map((a) => a.attivita_id);
    const from = ids.indexOf(srcId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const reordered = [...ids];
    reordered.splice(from, 1);
    reordered.splice(to, 0, srcId);
    await onReorder(reordered.map((aid, idx) => ({ attivita_id: aid, ordine: idx })));
  };

  /* -------- Modalità collega (dipendenze) -------- */
  const toggleLink = (a) => {
    if (!onLinkDeps || readOnly) return;
    if (!linkingFrom) {
      setLinkingFrom(a.attivita_id);
      return;
    }
    if (linkingFrom === a.attivita_id) {
      setLinkingFrom(null);
      return;
    }
    // Aggiungi linkingFrom come dipendenza di `a` (a dipende da linkingFrom)
    const existing = Array.isArray(a.dipendenze) ? a.dipendenze : [];
    if (existing.includes(linkingFrom)) {
      setLinkingFrom(null);
      return;
    }
    onLinkDeps({ attivita_id: a.attivita_id, dipendenze: [...existing, linkingFrom] });
    setLinkingFrom(null);
  };
  const removeDep = (target, depId) => {
    if (!onLinkDeps || readOnly) return;
    const existing = Array.isArray(target.dipendenze) ? target.dipendenze : [];
    onLinkDeps({ attivita_id: target.attivita_id, dipendenze: existing.filter((x) => x !== depId) });
  };

  /* -------- Item override in caso di resize preview -------- */
  const displayItems = useMemo(() => {
    if (!resizePreview) return items;
    return items.map((a) => a.attivita_id === resizePreview.id ? { ...a, data_inizio: resizePreview.di, data_fine: resizePreview.df } : a);
  }, [items, resizePreview]);

  /* -------- Dipendenze: calcolo coordinate SVG (in "unità 100" X + px Y) -------- */
  const depArrows = useMemo(() => {
    const out = [];
    const map = new Map(displayItems.map((a, i) => [a.attivita_id, { att: a, idx: i, box: activityBox(a, scale) }]));
    displayItems.forEach((a) => {
      const deps = Array.isArray(a.dipendenze) ? a.dipendenze : [];
      const target = map.get(a.attivita_id);
      if (!target || !target.box) return;
      deps.forEach((depId) => {
        const src = map.get(depId);
        if (!src || !src.box) return;
        const x1 = Math.max(0, Math.min(100, src.box.offsetPct + src.box.widthPct)); // fine barra sorgente
        const y1 = src.idx * ROW_H + ROW_H / 2;
        const x2 = Math.max(0, Math.min(100, target.box.offsetPct));                 // inizio target
        const y2 = target.idx * ROW_H + ROW_H / 2;
        out.push({ id: `${depId}->${a.attivita_id}`, x1, y1, x2, y2 });
      });
    });
    return out;
  }, [displayItems, scale]);

  return (
    <div className="border border-slate-200 rounded-sm bg-white overflow-x-auto" data-testid="gantt-timeline">
      {linkingFrom && (
        <div className="px-3 py-2 bg-blue-50 border-b border-blue-300 text-[11px] text-blue-900 font-semibold flex items-center gap-2" data-testid="gantt-linking-banner">
          <Link2 className="w-3.5 h-3.5" />
          Modalità collegamento attiva — clicca il <b>🔗</b> di un'altra lavorazione per creare una dipendenza (fine → inizio).
          <button onClick={() => setLinkingFrom(null)} className="ml-auto text-blue-800 hover:underline font-bold">Annulla</button>
        </div>
      )}

      <div style={{ minWidth: NAME_COL_PX + TIMELINE_MIN_PX }}>
        {/* ============ HEADER ============ */}
        <div className="flex border-b border-slate-200 bg-slate-50 h-8">
          <div
            className="sticky left-0 z-20 flex-shrink-0 bg-slate-800 text-white border-r-2 border-slate-400 flex"
            style={{ width: NAME_COL_PX }}
            data-testid="gantt-header-lavorazione"
          >
            <div
              className="flex items-center justify-center border-r border-slate-600 text-[10px] font-bold uppercase"
              style={{ width: NUM_COL_PX }}
            >
              N.
            </div>
            <div
              className="flex items-center px-3 border-r border-slate-600 text-[10px] font-bold uppercase tracking-wider"
              style={{ width: WORK_COL_PX }}
            >
              Lavorazione
            </div>
            <div
              className="flex items-center px-3 text-[10px] font-bold uppercase tracking-wider"
              style={{ width: RESP_COL_PX }}
            >
              Impresa / Responsabile
            </div>
          </div>
          <div className="relative flex-1" ref={timelineRef}>
            {scale.ticks.map((t, i) => (
              <div
                key={i}
                className={`absolute top-0 h-full flex items-center justify-center text-[9px] font-semibold uppercase border-l border-slate-300 ${
                  viewMode === "giornaliera" && t.isWeekend
                    ? "bg-slate-200 text-slate-700"
                    : "bg-slate-50 text-slate-700"
                }`}
                style={{ left: `${t.offsetPct}%`, width: `${t.widthPct}%` }}
                data-testid={`gantt-tick-${i}`}
              >
                {viewMode === "giornaliera" ? (
                  <div className="leading-tight text-center">
                    <div className="font-bold">{t.weekdayLabel}</div>
                    <div className="text-[8px]">{t.dateLabel}</div>
                  </div>
                ) : (
                  <span className="truncate px-1">{t.label}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ============ RIGHE ============ */}
        <DndContext sensors={sensors} onDragMove={(e) => setPendingDrag({ id: e.active.id, deltaPx: e.delta?.x || 0 })} onDragEnd={handleDragEnd}>
          <div className="relative">
            {displayItems.length === 0 && (
              <div className="p-6 text-center text-xs text-slate-400">
                Nessuna lavorazione. Aggiungine una nuova o genera le lavorazioni standard.
              </div>
            )}

            {/* SVG overlay per le frecce dipendenza */}
            {displayItems.length > 0 && depArrows.length > 0 && (
              <svg
                className="absolute pointer-events-none z-[5]"
                style={{ left: NAME_COL_PX, top: 0, right: 0, height: displayItems.length * ROW_H, width: `calc(100% - ${NAME_COL_PX}px)` }}
                viewBox={`0 0 100 ${displayItems.length * ROW_H}`}
                preserveAspectRatio="none"
                data-testid="gantt-deps-svg"
              >
                <defs>
                  <marker id="arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse" preserveAspectRatio="none">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#1e293b" />
                  </marker>
                </defs>
                {depArrows.map((a) => {
                  // Elbow path: dritto orizzontale + verticale + orizzontale corto
                  const midX = (a.x1 + a.x2) / 2;
                  const d = `M ${a.x1} ${a.y1} L ${midX} ${a.y1} L ${midX} ${a.y2} L ${a.x2} ${a.y2}`;
                  return (
                    <path
                      key={a.id}
                      d={d}
                      fill="none"
                      stroke="#1e293b"
                      strokeWidth="0.4"
                      vectorEffect="non-scaling-stroke"
                      markerEnd="url(#arr)"
                      data-testid={`gantt-arrow-${a.id}`}
                    />
                  );
                })}
              </svg>
            )}

            {displayItems.map((a) => {
              const box = activityBox(a, scale);
              const colore = a.colore_stato || "#94a3b8";
              const isLinkSrc = linkingFrom === a.attivita_id;
              const isDragOver = rowDrag.overId === a.attivita_id && rowDrag.srcId !== a.attivita_id;
              const isDragSrc = rowDrag.srcId === a.attivita_id;
              return (
                <div
                  key={a.attivita_id}
                  className={`flex border-t border-slate-100 ${a.is_in_ritardo ? "bg-rose-50/40" : "hover:bg-slate-50/70"} ${isDragOver ? "outline outline-2 outline-blue-400" : ""} ${isDragSrc ? "opacity-50" : ""}`}
                  data-testid={`gantt-row-${a.attivita_id}`}
                  onDragOver={handleRowDragOver(a.attivita_id)}
                  onDrop={handleRowDrop(a.attivita_id)}
                >
                  {/* ---- Colonne fisse stile cronoprogramma ---- */}
                  <div
                    className={`sticky left-0 z-10 flex-shrink-0 border-r-2 border-slate-300 flex ${
                      a.is_in_ritardo ? "bg-rose-50" : "bg-white"
                    } ${isLinkSrc ? "ring-2 ring-blue-500 ring-inset" : ""}`}
                    style={{ width: NAME_COL_PX }}
                  >
                    <div
                      className="border-r border-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-700"
                      style={{ width: NUM_COL_PX }}
                    >
                      {!readOnly && onReorder && (
                        <div
                          draggable
                          onDragStart={handleRowDragStart(a.attivita_id)}
                          onDragEnd={() => setRowDrag({ srcId: null, overId: null })}
                          className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 mr-1"
                          title="Trascina per riordinare"
                        >
                          <GripVertical className="w-3 h-3" />
                        </div>
                      )}
                      <span>{a.ordine !== undefined && a.ordine !== null ? a.ordine + 1 : ""}</span>
                    </div>

                    <div
                      className="border-r border-slate-200 px-2 flex items-center min-w-0"
                      style={{ width: WORK_COL_PX }}
                    >
                      <span
                        className="inline-block w-1 h-6 rounded-sm flex-shrink-0 mr-2"
                        style={{ background: colore }}
                        title={STATO_LABELS[a.stato] || a.stato}
                      />

                      <button
                        type="button"
                        onClick={() => onRowClick?.(a)}
                        className="flex-1 text-left text-[11px] font-semibold text-slate-800 hover:text-orange-700 min-w-0"
                        title={`${a.nome} · ${a.data_inizio} → ${a.data_fine}`}
                      >
                        <span className="block truncate">{a.nome}</span>
                        {a.categoria && (
                          <span className="block text-[8px] font-normal text-slate-500 uppercase tracking-wider truncate">
                            {a.categoria}
                          </span>
                        )}
                      </button>
                    </div>

                    <div
                      className="px-2 flex items-center gap-1 min-w-0"
                      style={{ width: RESP_COL_PX }}
                    >
                      <div className="flex-1 text-[10px] font-semibold text-slate-700 truncate">
                        {a.responsabile || "—"}
                      </div>

                      {!readOnly && onLinkDeps && (
                        <button
                          type="button"
                          onClick={() => toggleLink(a)}
                          title={isLinkSrc ? "Annulla collegamento" : "Collega dipendenza"}
                          className={`p-1 rounded-sm ${
                            isLinkSrc
                              ? "text-white bg-blue-600"
                              : "text-slate-500 hover:text-blue-700 hover:bg-blue-50"
                          }`}
                        >
                          <Link2 className="w-3 h-3" />
                        </button>
                      )}

                      {!readOnly && onEditRow && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditRow(a);
                          }}
                          title="Modifica"
                          className="p-1 text-slate-500 hover:text-blue-700"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}

                      {!readOnly && onDeleteRow && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteRow(a);
                          }}
                          title="Elimina"
                          className="p-1 text-slate-500 hover:text-rose-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ---- Timeline / barra ---- */}
                  <div className="relative flex-1 h-7">
                    {scale.ticks.map((t, i) => (
                      <div
                        key={i}
                        className={`absolute top-0 h-full border-l border-slate-200 pointer-events-none ${
                          viewMode === "giornaliera" && t.isWeekend
                            ? "bg-slate-100/80"
                            : ""
                        }`}
                        style={{
                          left: `${t.offsetPct}%`,
                          width: `${t.widthPct}%`
                        }}
                      />
                    ))}
                    {box ? (
                      <DraggableBar
                        activity={a}
                        box={box}
                        viewMode={viewMode}
                        onClick={onRowClick}
                        readOnly={readOnly}
                        onResizeStart={handleResizeStart}
                      />
                    ) : (
                      <div className="absolute left-2 top-1.5 text-[10px] text-slate-400 italic">
                        Fuori dal range · {a.data_inizio} → {a.data_fine}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </DndContext>

        {/* ============ LEGEND / FOOTER ============ */}
        <div className="flex border-t border-slate-200 bg-slate-50">
          <div
            className="sticky left-0 z-10 flex-shrink-0 bg-slate-50 border-r-2 border-slate-300 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500"
            style={{ width: NAME_COL_PX }}
          >
            Legenda
          </div>
          <div className="flex-1 px-3 py-2 text-[10px] text-slate-500 flex flex-wrap items-center gap-2">
            {Object.entries(STATO_LABELS).slice(0, 6).map(([k, l]) => (
              <span key={k} className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: {
                  programmata: "#94a3b8", in_corso: "#f59e0b", eseguita: "#10b981",
                  consegnato: "#3b82f6", attesa_materiale: "#eab308", sospesa: "#64748b",
                }[k] || "#94a3b8" }} />
                {l}
              </span>
            ))}
            {!readOnly && (
              <span className="ml-auto text-slate-400 hidden md:inline">
                💡 Trascina bordo per resize · grip per riordinare · 🔗 per collegare (snap: {snapText(viewMode)})
              </span>
            )}
          </div>
        </div>

        {/* Elenco dipendenze rimovibili */}
        {displayItems.some((a) => (a.dipendenze || []).length > 0) && !readOnly && (
          <div className="flex border-t border-slate-200 bg-slate-50/50">
            <div className="sticky left-0 z-10 flex-shrink-0 bg-slate-50/50 border-r-2 border-slate-300 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500" style={{ width: NAME_COL_PX }}>
              Dipendenze
            </div>
            <div className="flex-1 px-3 py-2 text-[10px] text-slate-600 flex flex-wrap gap-2">
              {displayItems.flatMap((a) => (a.dipendenze || []).map((depId) => {
                const src = displayItems.find((x) => x.attivita_id === depId);
                if (!src) return null;
                return (
                  <span key={`${depId}->${a.attivita_id}`} className="inline-flex items-center gap-1 bg-white border border-slate-300 rounded-sm px-1.5 py-0.5" data-testid={`dep-tag-${depId}-${a.attivita_id}`}>
                    <b>{src.nome}</b> → {a.nome}
                    <button onClick={() => removeDep(a, depId)} title="Rimuovi dipendenza" className="text-rose-600 hover:text-rose-800 ml-1"><Link2Off className="w-3 h-3" /></button>
                  </span>
                );
              }))}
            </div>
          </div>
        )}
      </div>

      {pendingDrag && (
        <div className="fixed bottom-4 right-4 z-50 bg-slate-900 text-white text-xs px-3 py-2 rounded-sm shadow-lg" data-testid="gantt-drag-indicator">
          Riprogrammazione in corso… snap: {snapText(viewMode)}
        </div>
      )}
      {resizePreview && (
        <div className="fixed bottom-4 right-4 z-50 bg-blue-900 text-white text-xs px-3 py-2 rounded-sm shadow-lg" data-testid="gantt-resize-indicator">
          {resizePreview.di} → {resizePreview.df}
        </div>
      )}
    </div>
  );
}

function snapText(vm) {
  if (vm === "giornaliera") return "1 giorno";
  if (vm === "settimanale") return "1 settimana";
  if (vm === "mensile") return "1 mese";
  if (vm === "trimestrale") return "1 trimestre";
  return "1 anno";
}

// Silence unused import warnings in some builds
void addDays; void isoDate;
